import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    Alert,
    Badge,
    Box,
    Button,
    Checkbox,
    Group,
    Radio,
    Select,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
    listDuplicateClusters,
    listOrganizations,
    mergeDuplicates,
    previewDuplicateMerge,
} from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { PoiDetailPanel } from '../../components/detail/PoiDetailPanel';
import { SubjectDetailPanel } from '../../components/detail/SubjectDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { CopyableId } from '../../components/CopyableText';
import type {
    DuplicateReviewCluster,
    DuplicateReviewEntityType,
    DuplicateReviewMergeEntityType,
    DuplicateReviewMergeResult,
} from '../../types/api';

const entityTypes: DuplicateReviewEntityType[] = ['subject', 'poi'];
const PAGE_SIZE = 10;

function isMergeEntity(entityType: DuplicateReviewEntityType): entityType is DuplicateReviewMergeEntityType {
    return entityType === 'subject' || entityType === 'poi';
}

function recordId(record: Record<string, unknown>): string {
    return typeof record.id === 'string' ? record.id : '';
}

function recordTitle(record: Record<string, unknown>, entityType: DuplicateReviewEntityType): string {
    if (typeof record.fullName === 'string' && record.fullName) {
        return record.fullName;
    }
    if (typeof record.name === 'string' && record.name) {
        return record.name;
    }
    return String(record.id ?? entityType);
}

function recordSecondary(record: Record<string, unknown>, entityType: DuplicateReviewEntityType): string | undefined {
    if (entityType === 'subject') {
        return [record.primaryPhone, record.claimedBusinessName].filter(Boolean).join(' · ') || undefined;
    }
    if (entityType === 'poi') {
        return [record.claimedAddress, record.poiType].filter(Boolean).join(' · ') || undefined;
    }
    return undefined;
}

function operationLabel(action: string): string {
    if (action === 'reassign') {
        return 'Relink rows to canonical record';
    }
    if (action === 'soft_delete_duplicates') {
        return 'Soft-delete duplicate link rows created by relinking';
    }
    if (action === 'soft_delete_entities') {
        return 'Soft-delete duplicate entity records';
    }
    return action;
}

function PreviewConsequences({ preview }: { preview: DuplicateReviewMergeResult }) {
    return (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Stack gap="sm">
                <Title order={5}>Consequences of this merge</Title>
                <Text size="sm">
                    The canonical record <strong>{preview.canonicalId}</strong> will stay active. The duplicate
                    records <strong>{preview.duplicateIds.join(', ')}</strong> will be soft-deleted after their
                    linked rows are moved.
                </Text>
                <Text size="sm">
                    Cases, visits, reports, facts, verifications, and relationship rows are not deleted; their
                    foreign keys are updated to point at the canonical record where listed below.
                </Text>
                <Table withTableBorder withColumnBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Table</Table.Th>
                            <Table.Th>Action</Table.Th>
                            <Table.Th>Rows affected</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {preview.operations.map((operation) => (
                            <Table.Tr key={`${operation.table}:${operation.action}`}>
                                <Table.Td>{operation.table}</Table.Td>
                                <Table.Td>{operationLabel(operation.action)}</Table.Td>
                                <Table.Td>{operation.count}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                {preview.warnings.length ? (
                    <Stack gap={4}>
                        {preview.warnings.map((warning) => (
                            <Text key={warning} size="sm" c="red">
                                {warning}
                            </Text>
                        ))}
                    </Stack>
                ) : null}
            </Stack>
        </Alert>
    );
}

function ClusterCard({
    cluster,
    selected,
    canonicalId,
    onSelect,
    onCanonicalChange,
    onOpenRecord,
}: {
    cluster: DuplicateReviewCluster;
    selected: boolean;
    canonicalId: string | null;
    onSelect: () => void;
    onCanonicalChange: (id: string) => void;
    onOpenRecord: (entityType: DuplicateReviewEntityType, id: string) => void;
}) {
    const canMerge = isMergeEntity(cluster.entityType);
    return (
        <DataPanel
            shadow={selected ? 'md' : 'xs'}
            style={{ borderColor: selected ? 'var(--mantine-color-violet-5)' : undefined }}
        >
            <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Group gap="xs">
                            <Badge color={canMerge ? 'violet' : 'gray'}>{cluster.entityType}</Badge>
                            <Badge variant="light">Score {cluster.score}</Badge>
                            {cluster.reasons.map((reason) => (
                                <Badge key={reason} variant="outline">
                                    {reason}
                                </Badge>
                            ))}
                        </Group>
                        <Text size="xs" c="dimmed">
                            {cluster.clusterId}
                        </Text>
                    </Stack>
                    <Button variant={selected ? 'filled' : 'light'} onClick={onSelect}>
                        Review
                    </Button>
                </Group>

                <Group gap="xs">
                    {Object.entries(cluster.impact).map(([key, value]) => (
                        <Badge key={key} variant="default">
                            {key}: {value}
                        </Badge>
                    ))}
                </Group>

                {canMerge ? (
                    <Radio.Group value={canonicalId ?? ''} onChange={onCanonicalChange} label="Canonical record">
                        <Stack gap="xs" mt="xs">
                            {cluster.records.map((record) => {
                                const id = recordId(record);
                                return (
                                    <Box
                                        key={id}
                                        component="button"
                                        type="button"
                                        onClick={() => onOpenRecord(cluster.entityType, id)}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            background: canonicalId === id ? 'var(--mantine-color-violet-light)' : 'transparent',
                                            border: '1px solid var(--mantine-color-default-border)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            cursor: 'pointer',
                                            padding: 'var(--mantine-spacing-xs)',
                                        }}
                                    >
                                        <Group align="flex-start" wrap="nowrap">
                                            <Radio
                                                value={id}
                                                aria-label={`Keep ${id} as canonical record`}
                                                onClick={(event) => event.stopPropagation()}
                                            />
                                            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                                <EntityRef
                                                    title={recordTitle(record, cluster.entityType)}
                                                    id={id}
                                                    secondary={recordSecondary(record, cluster.entityType)}
                                                    hideId
                                                />
                                                <CopyableId id={id} />
                                                <Text size="xs" c="dimmed">
                                                    Click this record to inspect full details before choosing what to keep.
                                                </Text>
                                            </Stack>
                                        </Group>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Radio.Group>
                ) : (
                    <Stack gap="xs">
                        {cluster.records.map((record) => {
                            const id = recordId(record);
                            return (
                                <Box
                                    key={id}
                                    component="button"
                                    type="button"
                                    onClick={() => onOpenRecord(cluster.entityType, id)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        background: 'transparent',
                                        border: '1px solid var(--mantine-color-default-border)',
                                        borderRadius: 'var(--mantine-radius-md)',
                                        cursor: 'pointer',
                                        padding: 'var(--mantine-spacing-xs)',
                                    }}
                                >
                                    <EntityRef
                                        title={recordTitle(record, cluster.entityType)}
                                        id={id}
                                        secondary={recordSecondary(record, cluster.entityType)}
                                        hideId
                                    />
                                    <CopyableId id={id} />
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </DataPanel>
    );
}

export function DuplicateReviewPage() {
    const { t } = useTranslation();
    const [entityType, setEntityType] = useState<DuplicateReviewEntityType>('subject');
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
    const [canonicalId, setCanonicalId] = useState<string | null>(null);
    const [detailRecord, setDetailRecord] = useState<{
        entityType: DuplicateReviewEntityType;
        id: string;
    } | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });

    const clustersQ = useQuery({
        queryKey: ['duplicate-review-clusters', entityType, organizationId, page],
        queryFn: () => listDuplicateClusters({ entityType, organizationId: organizationId!, page, limit: PAGE_SIZE }),
        enabled: Boolean(organizationId),
    });

    const clusterPage = clustersQ.data;
    const clusters = clusterPage?.data ?? [];
    const hasPreviousPage = page > 1;
    const hasNextPage = Boolean(clusterPage?.hasMore ?? (clusterPage && clusterPage.total > page * clusterPage.limit));
    const selectedCluster = clusters.find((cluster) => cluster.clusterId === selectedClusterId) ?? null;
    const duplicateIds = useMemo(() => {
        if (!selectedCluster || !canonicalId) {
            return [];
        }
        return selectedCluster.records.map(recordId).filter((id) => id && id !== canonicalId);
    }, [canonicalId, selectedCluster]);

    useEffect(() => {
        setSelectedClusterId(null);
        setCanonicalId(null);
        setAcknowledged(false);
        setPage(1);
    }, [entityType, organizationId]);

    useEffect(() => {
        setSelectedClusterId(null);
        setCanonicalId(null);
        setAcknowledged(false);
    }, [page]);

    const previewMutation = useMutation({
        mutationFn: previewDuplicateMerge,
        onSuccess: () => setAcknowledged(false),
        onError: (error: Error) => notifications.show({ color: 'red', message: error.message }),
    });

    const mergeMutation = useMutation({
        mutationFn: mergeDuplicates,
        onSuccess: () => {
            notifications.show({ color: 'green', message: 'Duplicate records merged' });
            setAcknowledged(false);
            setSelectedClusterId(null);
            setCanonicalId(null);
            previewMutation.reset();
            void clustersQ.refetch();
        },
        onError: (error: Error) => notifications.show({ color: 'red', message: error.message }),
    });

    const currentMergeRequest =
        selectedCluster && canonicalId && isMergeEntity(selectedCluster.entityType) && duplicateIds.length
            ? { entityType: selectedCluster.entityType, canonicalId, duplicateIds }
            : null;
    const preview = previewMutation.data;
    const previewMatchesSelection = Boolean(
        preview &&
            currentMergeRequest &&
            preview.entityType === currentMergeRequest.entityType &&
            preview.canonicalId === currentMergeRequest.canonicalId &&
            preview.duplicateIds.join('|') === currentMergeRequest.duplicateIds.join('|'),
    );

    return (
        <Stack gap="lg">
            <PageHeader
                title="Duplicate review"
                description="Choose an entity type and organization to surface suspected duplicate subjects or POIs. Preview the exact consequences before merging."
            />

            <DataPanel>
                <Stack gap="md">
                    <Group grow align="flex-end">
                        <Select
                            label="Entity type"
                            data={entityTypes.map((value) => ({ value, label: value }))}
                            value={entityType}
                            onChange={(value) => setEntityType((value ?? 'subject') as DuplicateReviewEntityType)}
                        />
                        <Select
                            label={t('fields.organization')}
                            placeholder="Select organization"
                            searchable
                            data={(orgsQ.data?.data ?? []).map((org) => ({ value: org.id, label: org.name }))}
                            value={organizationId}
                            onChange={setOrganizationId}
                        />
                    </Group>
                </Stack>
            </DataPanel>

            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
                <Text size="sm">
                    Select a cluster, inspect candidate records, choose the canonical record to keep, then preview
                    consequences. The merge button appears in the selected cluster controls and remains disabled
                    until the preview is acknowledged.
                </Text>
            </Alert>

            {selectedCluster && isMergeEntity(selectedCluster.entityType) ? (
                <DataPanel>
                    <Stack gap="md">
                        <Group justify="space-between" align="center">
                            <Stack gap={2}>
                                <Title order={4}>Selected cluster merge controls</Title>
                                <Text size="sm" c="dimmed">
                                    Canonical record to keep:
                                </Text>
                                {canonicalId ? <CopyableId id={canonicalId} /> : null}
                                <Text size="sm" c="dimmed">
                                    Duplicate records to relink and soft-delete: {duplicateIds.join(', ') || 'none'}
                                </Text>
                            </Stack>
                            <Button
                                variant="light"
                                disabled={!currentMergeRequest}
                                loading={previewMutation.isPending}
                                onClick={() => currentMergeRequest && previewMutation.mutate(currentMergeRequest)}
                            >
                                Preview consequences
                            </Button>
                        </Group>

                        {previewMatchesSelection && preview ? <PreviewConsequences preview={preview} /> : null}

                        <Checkbox
                            checked={acknowledged}
                            disabled={!previewMatchesSelection}
                            onChange={(event) => setAcknowledged(event.currentTarget.checked)}
                            label="I have reviewed the affected tables, row counts, canonical record, and duplicate records that will be soft-deleted."
                        />

                        <Group justify="flex-end">
                            <Button
                                color="red"
                                disabled={!currentMergeRequest || !previewMatchesSelection || !acknowledged}
                                loading={mergeMutation.isPending}
                                onClick={() => currentMergeRequest && mergeMutation.mutate(currentMergeRequest)}
                            >
                                Merge and relink records
                            </Button>
                        </Group>
                    </Stack>
                </DataPanel>
            ) : null}

                <Stack gap="md">
                    {organizationId ? (
                        <Group justify="space-between" align="center">
                            <Text size="sm" c="dimmed">
                                Page {page} · showing {clusters.length} clusters
                            </Text>
                            <Group gap="xs">
                                <Button
                                    variant="light"
                                    disabled={!hasPreviousPage || clustersQ.isLoading}
                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="light"
                                    disabled={!hasNextPage || clustersQ.isLoading}
                                    onClick={() => setPage((current) => current + 1)}
                                >
                                    Next
                                </Button>
                            </Group>
                        </Group>
                    ) : null}
                    {!organizationId ? (
                        <DataPanel>
                        <Text c="dimmed" ta="center" py="lg">
                            Select an organization to load duplicate clusters.
                        </Text>
                    </DataPanel>
                ) : null}
                {organizationId && clusters.length === 0 && !clustersQ.isLoading ? (
                    <DataPanel>
                        <Text c="dimmed" ta="center" py="lg">
                            No suspected duplicate clusters found.
                        </Text>
                    </DataPanel>
                ) : null}
                {clusters.map((cluster) => (
                    <ClusterCard
                        key={cluster.clusterId}
                        cluster={cluster}
                        selected={cluster.clusterId === selectedClusterId}
                        canonicalId={cluster.clusterId === selectedClusterId ? canonicalId : null}
                        onSelect={() => {
                            setSelectedClusterId(cluster.clusterId);
                            setCanonicalId(recordId(cluster.records[0] ?? {}));
                            setAcknowledged(false);
                            previewMutation.reset();
                        }}
                        onCanonicalChange={(id) => {
                            setCanonicalId(id);
                            setAcknowledged(false);
                            previewMutation.reset();
                        }}
                        onOpenRecord={(detailEntityType, id) => setDetailRecord({ entityType: detailEntityType, id })}
                    />
                ))}
            </Stack>
            <DetailDrawer
                opened={Boolean(detailRecord)}
                onClose={() => setDetailRecord(null)}
                title={detailRecord ? `Review ${detailRecord.entityType}` : 'Review record'}
                subtitle={detailRecord?.id}
            >
                {detailRecord?.entityType === 'subject' ? <SubjectDetailPanel subjectId={detailRecord.id} /> : null}
                {detailRecord?.entityType === 'poi' ? <PoiDetailPanel poiId={detailRecord.id} /> : null}
            </DetailDrawer>
        </Stack>
    );
}
